import type { ReactNode } from 'react';
import type { ClubAccessStatus } from '@bookclub/shared';
import type { ClubHostPublic } from '@bookclub/shared';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';
import { ReaderProgressBar } from '@/shared/components/reader/ReaderProgressBar';

interface ClubHeroProps {
  title: string;
  bookAuthor?: string | null;
  host?: ClubHostPublic | null;
  secondHost?: ClubHostPublic | null;
  coverUrl?: string | null;
  description?: string | null;
  accessStatus: ClubAccessStatus;
  progressPercent?: number | null;
  primaryActionLabel: string;
  onPrimaryAction?: () => void;
  badge?: ReactNode;
  leadingOverlay?: ReactNode;
  footer?: ReactNode;
  secondaryAction?: ReactNode;
  showPrimaryAction?: boolean;
  showHostSubtitle?: boolean;
  hostLayout?: 'inline' | 'card';
  descriptionHeading?: string | null;
}

export function ClubHero({
  title,
  bookAuthor,
  host,
  secondHost,
  coverUrl,
  description,
  accessStatus: _accessStatus,
  progressPercent,
  primaryActionLabel,
  onPrimaryAction,
  badge,
  leadingOverlay,
  footer,
  secondaryAction,
  showPrimaryAction = true,
  showHostSubtitle = true,
  hostLayout = 'inline',
  descriptionHeading,
}: ClubHeroProps) {
  const hosts = [host, secondHost].flatMap((clubHost) =>
    clubHost?.fullName
      ? [
          {
            fullName: clubHost.fullName,
            subtitle: clubHost.subtitle,
            description: clubHost.description,
            avatarUrl: clubHost.avatarUrl,
          },
        ]
      : [],
  );

  return (
    <MiniappSurface className="overflow-hidden bg-[linear-gradient(180deg,rgba(255,252,245,0.98)_0%,rgba(247,240,225,0.94)_100%)] p-0">
      <div className="min-[768px]:grid min-[768px]:grid-cols-[minmax(280px,0.92fr)_minmax(0,1.08fr)] min-[768px]:items-stretch">
        <div className="relative overflow-hidden px-5 pb-4 pt-3 min-[430px]:px-6 min-[430px]:pb-5 min-[430px]:pt-3.5 min-[768px]:flex min-[768px]:min-h-[30rem] min-[768px]:items-center min-[768px]:justify-center min-[768px]:px-8 min-[768px]:py-9">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.7),transparent_48%),linear-gradient(180deg,rgba(255,246,225,0.94),rgba(244,234,213,0.84))]" />

          {leadingOverlay ? (
            <div className="pointer-events-none absolute left-3 top-3 z-20 min-[430px]:left-4 min-[430px]:top-4">
              <div className="pointer-events-auto">{leadingOverlay}</div>
            </div>
          ) : null}

          <div className="relative flex justify-center pb-3 min-[430px]:pb-4 min-[768px]:pb-0">
            <div className="relative rounded-[30px] border border-white/55 bg-[rgba(255,251,243,0.56)] p-2 shadow-[0_24px_40px_rgba(62,48,23,0.14)] min-[430px]:rounded-[34px] min-[430px]:p-2.5">
              <div className="relative h-[172px] w-[136px] overflow-hidden rounded-[23px] bg-[linear-gradient(180deg,#d1bf95_0%,#f0e2c8_100%)] min-[430px]:h-[186px] min-[430px]:w-[148px] min-[430px]:rounded-[25px] min-[768px]:h-[300px] min-[768px]:w-[238px] min-[768px]:rounded-[30px]">
                {coverUrl ? (
                  <img src={coverUrl} alt={title} className="h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.52),transparent_55%),linear-gradient(180deg,rgba(147,111,53,0.38),rgba(244,234,213,0.78))]" />
                )}
              </div>
            </div>

            {badge ? (
              <div className="absolute left-3 top-2 min-[430px]:left-4 min-[430px]:top-2.5">{badge}</div>
            ) : null}
          </div>
        </div>

        <div className="px-6 pb-8 min-[430px]:px-7 min-[430px]:pb-9 min-[768px]:px-8 min-[768px]:py-10">
          <div className="mx-auto max-w-[23.5rem] px-1 min-[430px]:max-w-[24rem] min-[430px]:px-2 min-[768px]:mx-0 min-[768px]:max-w-none min-[768px]:px-0">
          <div className="h-3 min-[430px]:h-4 min-[768px]:hidden" aria-hidden="true" />
          <h1 className="mx-auto max-w-[18rem] text-center text-[17px] font-black uppercase leading-[0.97] tracking-[-0.04em] text-balance text-[var(--foreground)] min-[430px]:max-w-[19rem] min-[430px]:text-[19px] min-[768px]:mx-0 min-[768px]:max-w-[34rem] min-[768px]:text-left min-[768px]:text-[28px]">
            {title}
          </h1>
          {bookAuthor ? (
            <>
              <div className="h-3 min-[430px]:h-3.5" aria-hidden="true" />
              <p className="text-center text-[12px] font-black uppercase tracking-[0.04em] text-[var(--accent)] min-[430px]:text-[12.5px] min-[768px]:text-left">
                {bookAuthor}
              </p>
            </>
          ) : null}
          {hosts.length > 0 ? (
            <>
              <div className="h-4 min-[430px]:h-5" aria-hidden="true" />
              {hostLayout === 'card' ? (
                <div className="mx-auto flex max-w-[21rem] flex-col gap-3 px-4 text-left min-[430px]:max-w-[21.5rem] min-[430px]:gap-3.5 min-[430px]:px-5 min-[768px]:mx-0 min-[768px]:max-w-[34rem] min-[768px]:px-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[rgba(94,81,58,0.52)]">
                    {hosts.length > 1 ? 'КЛУБ ВЕДУТ' : 'КЛУБ ВЕДЁТ'}
                  </p>
                  <div className="overflow-hidden rounded-[24px] border border-white/60 bg-[rgba(255,250,241,0.84)] px-4 py-4 shadow-[0_16px_32px_rgba(62,48,23,0.08)] min-[430px]:rounded-[26px] min-[430px]:px-5 min-[430px]:py-5">
                    <div className="space-y-4 min-[430px]:space-y-5">
                      {hosts.map((host, index) => {
                        const hostDetails = host.description?.trim() ? host.description : host.subtitle;

                        return (
                          <div
                            key={host.fullName}
                            className={index > 0 ? 'border-t border-[rgba(94,81,58,0.12)] pt-4 min-[430px]:pt-5' : ''}
                          >
                            <div className="flex items-center justify-start gap-3 min-[430px]:gap-3.5">
                              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[rgba(26,26,26,0.08)] shadow-[0_10px_18px_rgba(62,48,23,0.1)] min-[430px]:h-12 min-[430px]:w-12">
                                {host.avatarUrl ? (
                                  <img
                                    src={host.avatarUrl}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <span className="text-[13px] font-bold uppercase text-[rgba(26,26,26,0.58)]">
                                    {host.fullName
                                      .split(' ')
                                      .slice(0, 2)
                                      .map((part) => part[0])
                                      .join('')}
                                  </span>
                                )}
                              </div>
                              <p className="text-[16px] font-semibold leading-[1.08] text-[var(--foreground)] min-[430px]:text-[17px]">
                                {host.fullName}
                              </p>
                            </div>
                            {hostDetails ? (
                              <p className="whitespace-pre-line pt-3 text-[13px] leading-[1.5] text-[rgba(26,26,26,0.62)] min-[430px]:pt-3.5 min-[430px]:text-[13.25px]">
                                {hostDetails}
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mx-auto flex max-w-[17.5rem] flex-col gap-3 px-2 text-left min-[430px]:max-w-[18rem] min-[430px]:gap-3.5 min-[430px]:px-3 min-[768px]:mx-0 min-[768px]:max-w-[34rem] min-[768px]:px-0">
                  {hosts.map((host) => (
                    <div key={host.fullName} className="flex items-start justify-start gap-3 min-[430px]:gap-3.5">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[rgba(26,26,26,0.08)] shadow-[0_10px_18px_rgba(62,48,23,0.1)] min-[430px]:h-12 min-[430px]:w-12">
                        {host.avatarUrl ? (
                          <img
                            src={host.avatarUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="text-[13px] font-bold uppercase text-[rgba(26,26,26,0.58)]">
                            {host.fullName
                              .split(' ')
                              .slice(0, 2)
                              .map((part) => part[0])
                              .join('')}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-[16px] font-semibold leading-[1.04] text-[var(--foreground)] min-[430px]:text-[17px]">
                          {host.fullName}
                        </p>
                        {showHostSubtitle && host.subtitle ? (
                          <p className="text-[12px] leading-[1.22] text-[rgba(26,26,26,0.46)] min-[430px]:text-[12.5px]">
                            {host.subtitle}
                          </p>
                        ) : null}
                        {host.description ? (
                          <p className="whitespace-pre-line pt-1 text-[13px] leading-[1.45] text-[rgba(26,26,26,0.62)] min-[430px]:text-[13.25px]">
                            {host.description}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>

        {progressPercent !== null && progressPercent !== undefined && progressPercent > 0 ? (
          <div className="mx-auto max-w-[21rem] px-4 pt-5 min-[430px]:max-w-[21.5rem] min-[430px]:px-5 min-[430px]:pt-6 min-[768px]:mx-0 min-[768px]:max-w-[34rem] min-[768px]:px-0">
            <ReaderProgressBar value={progressPercent} label="Прочитано" tone="accent" />
          </div>
        ) : null}

        {description ? (
          <div className="mx-auto max-w-[21rem] px-4 pt-5 min-[430px]:max-w-[21.5rem] min-[430px]:px-5 min-[430px]:pt-6 min-[768px]:mx-0 min-[768px]:max-w-[34rem] min-[768px]:px-0">
            {descriptionHeading ? (
              <p className="pb-3 text-[11px] font-black uppercase tracking-[0.18em] text-[rgba(94,81,58,0.52)]">
                {descriptionHeading}
              </p>
            ) : null}
            <p className="whitespace-pre-line text-left text-[14px] leading-[1.52] text-[rgba(26,26,26,0.68)] min-[430px]:text-[14.25px] min-[768px]:text-[15px]">
              {description}
            </p>
          </div>
        ) : null}

        {showPrimaryAction ? (
          <div className="mx-auto flex max-w-[24.75rem] flex-col gap-3 px-3 pt-10 min-[430px]:flex-row min-[430px]:px-4 min-[430px]:pt-11">
            <PrimaryButton onClick={onPrimaryAction} className="w-full min-[430px]:flex-1" tone="dark">
              {primaryActionLabel}
            </PrimaryButton>
            {secondaryAction ? <div className="min-[430px]:flex-1">{secondaryAction}</div> : null}
          </div>
        ) : null}

        {footer ? (
          <div className="mx-auto w-full max-w-[24.75rem] px-3 pt-10 text-left min-[430px]:px-4 min-[430px]:pt-11">
            {footer}
          </div>
        ) : null}
        </div>
      </div>
    </MiniappSurface>
  );
}
